import React, { useState, useEffect } from 'react';
import { Users, Calendar, Video, MessageSquare, Heart, UserPlus, Activity, BookOpen, Award } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import AdminLocalLayout from '../../components/AdminLocalLayout';
import AdminDashboardCard from '../../components/AdminDashboardCard';
import AdminDashboardChart from '../../components/AdminDashboardChart';

interface Stats {
  totalUsers: number;
  totalEvents: number;
  totalLiveStreams: number;
  totalMessages: number;
  totalPrayerRequests: number;
  totalSmallGroups: number;
  totalMinistries: number;
  totalCursos: number;
  totalTestimonials: number;
  userGrowth: number;
  eventGrowth: number;
  prayerGrowth: number;
}

export default function AdminLocalDashboard() {
  const [stats, setStats] = useState<Stats>({
    totalUsers: 0,
    totalEvents: 0,
    totalLiveStreams: 0,
    totalMessages: 0,
    totalPrayerRequests: 0,
    totalSmallGroups: 0,
    totalMinistries: 0,
    totalCursos: 0,
    totalTestimonials: 0,
    userGrowth: 0,
    eventGrowth: 0,
    prayerGrowth: 0
  });
  const [loading, setLoading] = useState(true);
  const [usersByMonthLabels, setUsersByMonthLabels] = useState<string[]>([]);
  const [usersByMonthData, setUsersByMonthData] = useState<any[]>([]);
  const [prayersByMonthLabels, setPrayersByMonthLabels] = useState<string[]>([]);
  const [prayersByMonthData, setPrayersByMonthData] = useState<any[]>([]);
  const [recentActivities, setRecentActivities] = useState<any[]>([]);
  const [igreja_id, setIgrejaId] = useState<string | null>(null);

  useEffect(() => {
    getUserIgreja();
  }, []);

  useEffect(() => {
    if (igreja_id) {
      fetchStats();
      fetchUsersByMonth();
      fetchPrayersByMonth();
      fetchRecentActivities();
    }
  }, [igreja_id]);

  async function getUserIgreja() {
    try {
      // Obter o usuário atual
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      
      // Obter a igreja do usuário (assumindo que o ID da igreja está nos metadados do usuário)
      const igreja_id = user.user_metadata?.igreja_id;
      
      if (!igreja_id) {
        console.error('Usuário não possui igreja associada');
        return;
      }
      
      setIgrejaId(igreja_id);
    } catch (error) {
      console.error('Erro ao obter igreja do usuário:', error);
    }
  }

  async function fetchStats() {
    try {
      setLoading(true);
      
      if (!igreja_id) return;
      
      // Fetch counts from various tables filtered by igreja_id
      const [
        { count: usersCount },
        { count: eventsCount },
        { count: liveStreamsCount },
        { count: messagesCount },
        { count: prayerRequestsCount },
        { count: smallGroupsCount },
        { count: ministriesCount },
        { count: cursosCount },
        { count: testimonialsCount }
      ] = await Promise.all([
        supabase.from('users').select('*', { count: 'exact', head: true }).eq('igreja_id', igreja_id),
        supabase.from('events').select('*', { count: 'exact', head: true }).eq('igreja_id', igreja_id),
        supabase.from('live_streams').select('*', { count: 'exact', head: true }).eq('igreja_id', igreja_id),
        supabase.from('messages').select('*', { count: 'exact', head: true }).eq('igreja_id', igreja_id),
        supabase.from('prayer_requests').select('*', { count: 'exact', head: true }).eq('igreja_id', igreja_id),
        supabase.from('small_groups').select('*', { count: 'exact', head: true }).eq('igreja_id', igreja_id),
        supabase.from('ministries').select('*', { count: 'exact', head: true }).eq('igreja_id', igreja_id),
        supabase.from('courses').select('*', { count: 'exact', head: true }).eq('igreja_id', igreja_id),
        supabase.from('testimonials').select('*', { count: 'exact', head: true }).eq('igreja_id', igreja_id)
      ]);

      // Calculate growth (example - you would need to implement the real logic)
      const userGrowth = 12; // Growth of 12% compared to previous month
      const eventGrowth = 5;
      const prayerGrowth = 8;

      setStats({
        totalUsers: usersCount || 0,
        totalEvents: eventsCount || 0,
        totalLiveStreams: liveStreamsCount || 0,
        totalMessages: messagesCount || 0,
        totalPrayerRequests: prayerRequestsCount || 0,
        totalSmallGroups: smallGroupsCount || 0,
        totalMinistries: ministriesCount || 0,
        totalCursos: cursosCount || 0,
        totalTestimonials: testimonialsCount || 0,
        userGrowth,
        eventGrowth,
        prayerGrowth
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  }

  async function fetchUsersByMonth() {
    // Mock data for users by month
    const labels = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun'];
    const data = [
      { month: 'Jan', users: 30 },
      { month: 'Fev', users: 42 },
      { month: 'Mar', users: 55 },
      { month: 'Abr', users: 70 },
      { month: 'Mai', users: 85 },
      { month: 'Jun', users: 95 },
    ];
    
    setUsersByMonthLabels(labels);
    setUsersByMonthData(data);
  }

  async function fetchPrayersByMonth() {
    // Mock data for prayers by month
    const labels = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun'];
    const data = [
      { month: 'Jan', prayers: 15 },
      { month: 'Fev', prayers: 22 },
      { month: 'Mar', prayers: 30 },
      { month: 'Abr', prayers: 35 },
      { month: 'Mai', prayers: 42 },
      { month: 'Jun', prayers: 50 },
    ];
    
    setPrayersByMonthLabels(labels);
    setPrayersByMonthData(data);
  }

  async function fetchRecentActivities() {
    // Mock data for recent activities
    const activities = [
      { 
        id: 1, 
        type: 'user_created', 
        description: 'Novo usuário registrado: João Silva', 
        timestamp: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
        icon: UserPlus,
        color: 'bg-blue-100 text-blue-600'
      },
      { 
        id: 2, 
        type: 'event_created', 
        description: 'Novo evento criado: Culto de Jovens', 
        timestamp: new Date(Date.now() - 60 * 60 * 1000).toISOString(),
        icon: Calendar,
        color: 'bg-green-100 text-green-600'
      },
      { 
        id: 3, 
        type: 'prayer_request', 
        description: 'Novo pedido de oração recebido', 
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
        icon: Heart,
        color: 'bg-red-100 text-red-600'
      },
      { 
        id: 4, 
        type: 'course_completed', 
        description: 'Maria Santos concluiu o curso "Discipulado"', 
        timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
        icon: Award,
        color: 'bg-purple-100 text-purple-600'
      }
    ];
    
    setRecentActivities(activities);
  }

  const formatTimeAgo = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.round(diffMs / 60000);
    
    if (diffMins < 60) {
      return `Há ${diffMins} ${diffMins === 1 ? 'minuto' : 'minutos'}`;
    }
    
    const diffHours = Math.round(diffMins / 60);
    if (diffHours < 24) {
      return `Há ${diffHours} ${diffHours === 1 ? 'hora' : 'horas'}`;
    }
    
    const diffDays = Math.round(diffHours / 24);
    return `Há ${diffDays} ${diffDays === 1 ? 'dia' : 'dias'}`;
  };

  const cards = [
    {
      title: 'Usuários',
      value: stats.totalUsers,
      icon: Users,
      color: 'bg-blue-100 text-blue-600',
      change: { value: stats.userGrowth, type: 'increase' as const }
    },
    {
      title: 'Eventos',
      value: stats.totalEvents,
      icon: Calendar,
      color: 'bg-green-100 text-green-600',
      change: { value: stats.eventGrowth, type: 'increase' as const }
    },
    {
      title: 'Transmissões',
      value: stats.totalLiveStreams,
      icon: Video,
      color: 'bg-red-100 text-red-600'
    },
    {
      title: 'Mensagens',
      value: stats.totalMessages,
      icon: MessageSquare,
      color: 'bg-purple-100 text-purple-600'
    },
    {
      title: 'Pedidos de Oração',
      value: stats.totalPrayerRequests,
      icon: Heart,
      color: 'bg-pink-100 text-pink-600',
      change: { value: stats.prayerGrowth, type: 'increase' as const }
    },
    {
      title: 'Pequenos Grupos',
      value: stats.totalSmallGroups,
      icon: Users,
      color: 'bg-indigo-100 text-indigo-600'
    },
    {
      title: 'Ministérios',
      value: stats.totalMinistries,
      icon: UserPlus,
      color: 'bg-amber-100 text-amber-600'
    },
    {
      title: 'Cursos',
      value: stats.totalCursos,
      icon: BookOpen,
      color: 'bg-teal-100 text-teal-600'
    }
  ];

  return (
    <AdminLocalLayout title="Dashboard Local">
      {/* Cards */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {cards.map((card) => (
          <AdminDashboardCard
            key={card.title}
            title={card.title}
            value={card.value}
            icon={card.icon}
            color={card.color}
            loading={loading}
            change={card.change}
          />
        ))}
      </div>

      {/* Charts */}
      <div className="mt-8 grid gap-6 md:grid-cols-2">
        <AdminDashboardChart 
          title="Crescimento de Usuários" 
          data={usersByMonthData} 
          labels={usersByMonthLabels}
          dataKey="users" 
          type="line"
          loading={loading}
        />

        <AdminDashboardChart 
          title="Pedidos de Oração" 
          data={prayersByMonthData} 
          labels={prayersByMonthLabels}
          dataKey="prayers" 
          type="bar"
          color="#EC4899"
          loading={loading}
        />
      </div>

      {/* Recent Activity */}
      <div className="mt-8 bg-white p-6 rounded-xl shadow-elevation-1">
        <h2 className="text-lg font-semibold text-brand-teal mb-4 flex items-center gap-2">
          <Activity className="h-5 w-5" />
          Atividade Recente
        </h2>
        
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-teal"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {recentActivities.map((activity) => (
              <div key={activity.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <div className={`p-2 rounded-lg ${activity.color}`}>
                  <activity.icon className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{activity.description}</p>
                  <p className="text-sm text-gray-500">{formatTimeAgo(activity.timestamp)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLocalLayout>
  );
}